/**
 * Consent-gated LinkedIn Insight Tag (insight.min.js) and conversions (#2445).
 *
 * Same grant as Google Analytics and the X pixel: `budgit_analytics_consent`
 * must be `granted`, and token-link pages never load it (#1216). Nothing
 * here creates `window.lintrk`, sets `_linkedin_partner_id`, or inserts the
 * script before that grant. With no `REACT_APP_LINKEDIN_PARTNER_ID` every
 * function is a no-op that returns false.
 */

import { getAnalyticsConsent, subscribeAnalyticsConsent } from './analyticsConsent';
import { isAnalyticsExcludedPath } from '../utils/sensitiveLinkToken';
import { registerStoreRedirectHook } from './storeNavigation';
import { getLinkedInInsightIds, LINKEDIN_INSIGHT_SCRIPT_SRC } from './linkedInInsightTagConfig';
import { markThirdPartyScriptInjected } from './reloadOnConsentWithdrawal';

const FIRED_STORAGE_PREFIX = 'linkedin_insight_fired:';

let tagLoadStarted = false;
const firedKeys = new Set();
/** @type {Map<string, () => boolean>} */
const pendingUntilConsent = new Map();
let consentListenerInstalled = false;
let storeHookInstalled = false;

function pathname() {
  if (typeof window === 'undefined' || !window.location) return '';
  return window.location.pathname || '';
}

function consentGranted() {
  return getAnalyticsConsent() === 'granted';
}

export function linkedInInsightMayLoad() {
  if (typeof window === 'undefined') return false;
  if (!getLinkedInInsightIds().partnerId) return false;
  if (!consentGranted()) return false;
  if (isAnalyticsExcludedPath(pathname())) return false;
  return true;
}

function installLintrkStub() {
  if (typeof window === 'undefined' || typeof window.lintrk === 'function') return;
  const lintrk = function lintrkStub(a, b) {
    lintrk.q.push([a, b]);
  };
  lintrk.q = [];
  window.lintrk = lintrk;
}

function hasFired(onceKey) {
  if (firedKeys.has(onceKey)) return true;
  try {
    if (window.sessionStorage && window.sessionStorage.getItem(FIRED_STORAGE_PREFIX + onceKey)) {
      firedKeys.add(onceKey);
      return true;
    }
  } catch (_) {
    // Private mode: the in-memory set is the only guard.
  }
  return false;
}

function markFired(onceKey) {
  firedKeys.add(onceKey);
  try {
    if (window.sessionStorage) {
      window.sessionStorage.setItem(FIRED_STORAGE_PREFIX + onceKey, '1');
    }
  } catch (_) {
    // Ignore quota / private mode.
  }
}

/**
 * Set the partner id and load insight.min.js. Idempotent.
 * Returns false, and touches nothing on window, until analytics consent is
 * granted and a partner id is configured.
 */
export function enableLinkedInInsightTag() {
  if (!linkedInInsightMayLoad()) return false;
  const { partnerId } = getLinkedInInsightIds();

  window._linkedin_partner_id = partnerId;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  if (!window._linkedin_data_partner_ids.includes(partnerId)) {
    window._linkedin_data_partner_ids.push(partnerId);
  }
  installLintrkStub();

  if (!tagLoadStarted) {
    const existing = document.querySelector(`script[src="${LINKEDIN_INSIGHT_SCRIPT_SRC}"]`);
    if (!existing) {
      const script = document.createElement('script');
      script.async = true;
      script.src = LINKEDIN_INSIGHT_SCRIPT_SRC;
      document.head.appendChild(script);
    }
    tagLoadStarted = true;
    markThirdPartyScriptInjected();
  }
  return true;
}

/**
 * lintrk('track', { conversion_id }). No-ops before consent and when the
 * conversion id is not configured. `onceKey` makes a repeated trigger a
 * no-op for the rest of the tab session. Vendor code never throws out of
 * here: a failed call is not marked fired and the caller sees false.
 */
export function trackLinkedInConversion(conversionId, { onceKey } = {}) {
  if (!conversionId || typeof window === 'undefined') return false;
  if (onceKey && hasFired(onceKey)) return false;
  try {
    if (!enableLinkedInInsightTag()) return false;
    if (typeof window.lintrk !== 'function') return false;
    window.lintrk('track', { conversion_id: Number(conversionId) });
  } catch (_) {
    // The Insight Tag must never surface as a signup, store, or checkout error.
    return false;
  }
  if (onceKey) markFired(onceKey);
  return true;
}

function queueUntilConsent(onceKey, fire) {
  if (typeof window === 'undefined') return false;
  if (!getLinkedInInsightIds().partnerId) return false;
  if (hasFired(onceKey)) return false;
  if (isAnalyticsExcludedPath(pathname())) return false;
  if (!consentGranted()) {
    // No decision yet: hold the event until Accept. A reject drops it.
    if (getAnalyticsConsent() == null) pendingUntilConsent.set(onceKey, fire);
    return false;
  }
  return fire();
}

/** Fire conversions that happened before Accept, only after the grant. */
export function flushLinkedInEventsAfterConsent() {
  if (!linkedInInsightMayLoad() || pendingUntilConsent.size === 0) return;
  const jobs = Array.from(pendingUntilConsent.values());
  pendingUntilConsent.clear();
  jobs.forEach((fire) => {
    try {
      fire();
    } catch (_) {
      // One event must not block the rest.
    }
  });
}

function installConsentListener() {
  if (consentListenerInstalled || typeof window === 'undefined') return;
  consentListenerInstalled = true;
  subscribeAnalyticsConsent((choice) => {
    if (choice === 'granted') {
      enableLinkedInInsightTag();
      flushLinkedInEventsAfterConsent();
    } else {
      // Reject drops everything that was held for Accept.
      pendingUntilConsent.clear();
    }
  });
}

installConsentListener();

/**
 * New web account finished PIN setup. A returning login does not call this.
 * The payload is only the conversion id; no account id leaves the browser.
 */
export function trackLinkedInWebSignup() {
  return queueUntilConsent('signup', () => trackLinkedInConversion(
    getLinkedInInsightIds().conversionIds.SIGNUP,
    { onceKey: 'signup' }
  ));
}

/**
 * Download-page store button or mobile auto-redirect. Reached through the
 * beforeStoreRedirect hook, which storeNavigation only runs with consent.
 */
export function trackLinkedInStoreClick(platform) {
  const store = platform === 'android' ? 'play_store' : 'app_store';
  const onceKey = `store_click:${store}`;
  return queueUntilConsent(onceKey, () => trackLinkedInConversion(
    getLinkedInInsightIds().conversionIds.STORE_CLICK,
    { onceKey }
  ));
}

/**
 * Stripe verify-session succeeded. `localDedupeKey` (the Checkout session
 * id) stays in this tab and is never put on the LinkedIn payload.
 */
export function trackLinkedInCheckoutSuccess({ localDedupeKey } = {}) {
  const onceKey = `checkout:${localDedupeKey || 'once'}`;
  return queueUntilConsent(onceKey, () => trackLinkedInConversion(
    getLinkedInInsightIds().conversionIds.CHECKOUT,
    { onceKey }
  ));
}

/**
 * Register the store-click conversion on the shared beforeStoreRedirect
 * seam. lintrk is fire-and-forget, so the hook resolves at once and never
 * uses the 300 ms redirect budget. Idempotent; a no-op when unconfigured.
 */
export function registerLinkedInStoreClickHook() {
  if (storeHookInstalled || typeof window === 'undefined') return false;
  if (!getLinkedInInsightIds().partnerId) return false;
  storeHookInstalled = true;
  registerStoreRedirectHook(({ platform }) => {
    trackLinkedInStoreClick(platform);
  });
  return true;
}

/** Boot-time policy: load only if previously granted, and wire the store hook. */
export function bootLinkedInInsightTagFromConsent() {
  registerLinkedInStoreClickHook();
  if (consentGranted()) {
    enableLinkedInInsightTag();
    flushLinkedInEventsAfterConsent();
  }
}

export function resetLinkedInInsightTagForTests() {
  tagLoadStarted = false;
  storeHookInstalled = false;
  firedKeys.clear();
  pendingUntilConsent.clear();
  if (typeof window !== 'undefined') {
    delete window.lintrk;
    delete window._linkedin_partner_id;
    delete window._linkedin_data_partner_ids;
  }
}
